import type { TimeseriesPoint } from "@codway-dev/shared";

export type TimeseriesMetricKey = keyof Omit<TimeseriesPoint, "date">;

export type ChartSeriesPoint = {
  date: string;
  value: number | null;
};

/** Normalizes PDP conversion for display; >100% omitted (—), otherwise whole percent. */
export function displayProductPageConversionRate(
  value: number | null | undefined
): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  if (value > 100) return null;
  return Math.round(value);
}

/**
 * Valor diario para el gráfico. Devuelve null cuando no hay dato real
 * (p. ej. conversión sin impresiones) para que Recharts una con connectNulls.
 */
export function toChartSeriesValue(
  point: TimeseriesPoint,
  dataKey: TimeseriesMetricKey
): number | null {
  if (dataKey === "conversion_rate") {
    const impressions = point.impressions ?? 0;
    if (impressions <= 0) return null;
    const value =
      point.conversion_rate ??
      Math.round(((point.completed_orders ?? 0) / impressions) * 10_000) / 100;
    return Number.isFinite(value) ? value : null;
  }

  if (dataKey === "product_page_conversion_rate") {
    const productPageViews = point.product_page_views ?? 0;
    if (productPageViews <= 0) return null;
    const raw =
      point.product_page_conversion_rate ??
      Math.round(((point.submissions ?? 0) / productPageViews) * 10_000) / 100;
    return displayProductPageConversionRate(raw);
  }

  if (dataKey === "avg_order_value") {
    const completedOrders = point.completed_orders ?? 0;
    if (completedOrders <= 0) return null;
    const value =
      point.avg_order_value ??
      Math.round(((point.total_revenue ?? 0) / completedOrders) * 100) / 100;
    return Number.isFinite(value) ? value : null;
  }

  const raw = point[dataKey];
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
}

export function mapTimeseriesToChartSeries(
  points: TimeseriesPoint[],
  dataKey: TimeseriesMetricKey
): ChartSeriesPoint[] {
  return points.map((point) => ({
    date: point.date,
    value: toChartSeriesValue(point, dataKey),
  }));
}

/** Metric keys rendered on the analytics dashboard charts. */
export const DASHBOARD_CHART_METRIC_KEYS = [
  "impressions",
  "completed_orders",
  "conversion_rate",
  "product_page_views",
  "product_page_conversion_rate",
  "total_revenue",
  "avg_order_value",
] as const satisfies readonly TimeseriesMetricKey[];

export type DashboardChartMetricKey =
  (typeof DASHBOARD_CHART_METRIC_KEYS)[number];

export function buildDashboardChartSeriesByMetric(
  points: TimeseriesPoint[]
): Record<DashboardChartMetricKey, ChartSeriesPoint[]> {
  return {
    impressions: mapTimeseriesToChartSeries(points, "impressions"),
    completed_orders: mapTimeseriesToChartSeries(points, "completed_orders"),
    conversion_rate: mapTimeseriesToChartSeries(points, "conversion_rate"),
    product_page_views: mapTimeseriesToChartSeries(
      points,
      "product_page_views"
    ),
    product_page_conversion_rate: mapTimeseriesToChartSeries(
      points,
      "product_page_conversion_rate"
    ),
    total_revenue: mapTimeseriesToChartSeries(points, "total_revenue"),
    avg_order_value: mapTimeseriesToChartSeries(points, "avg_order_value"),
  };
}
